/**
 * Shared helpers for the ALL CAPS custom sections (Deal Me One, Find Your Cap).
 * Depends on Dawn's own globals: routes, fetchConfig, publish, PUB_SUB_EVENTS.
 */
window.ACShared = (function () {
  function splitPhrase(str) {
    const words = str.toUpperCase().split(' ');
    if (words.length <= 2) return [words.join(' ')];
    if (words.length === 3) return [words.slice(0, 2).join(' '), words[2]];
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  }

  // Deterministic hash so the same product always renders the same placeholder colours.
  function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  function placeholderPalette(seed) {
    const hue = seed % 360;
    return {
      colour: `hsl(${hue}, 32%, 78%)`,
      brim: `hsl(${hue}, 32%, 64%)`,
      text: hue > 40 && hue < 200 ? '#111111' : '#f4efe6',
    };
  }

  function capSVG(product) {
    if (product.image) {
      return `<img src="${product.image}" alt="${escapeHtml(product.title)}" loading="lazy" class="ac-cap-image">`;
    }
    const seed = hashString(product.title || product.id + '');
    const palette = placeholderPalette(seed);
    const lines = splitPhrase(product.title || '');
    const fs = lines.join('').length > 20 ? 20 : 24;
    const y0 = lines.length === 1 ? 308 : 292;
    const textEls = lines
      .map(
        (l, i) =>
          `<text x="300" y="${y0 + i * 34}" font-family="Arial" font-size="${fs}" font-weight="900" text-anchor="middle" fill="${palette.text}">${escapeHtml(l)}</text>`
      )
      .join('');
    return `<svg viewBox="0 0 600 600" aria-hidden="true" class="ac-cap-svg">
      <ellipse cx="302" cy="474" rx="170" ry="38" fill="rgba(0,0,0,.12)"/>
      <path d="M145 330 C145 190,455 190,455 330 L439 406 C420 447,184 447,164 406 Z" fill="${palette.colour}" stroke="rgba(0,0,0,.34)" stroke-width="4"/>
      <path d="M305 330 C379 323,500 349,523 394 C482 423,376 433,275 407 C271 381,280 352,305 330 Z" fill="${palette.brim}" stroke="rgba(0,0,0,.34)" stroke-width="4"/>
      <path d="M300 194 L300 406" stroke="rgba(255,255,255,.18)" stroke-width="2"/>
      <path d="M180 256 Q300 185 420 256" stroke="rgba(255,255,255,.18)" stroke-width="2" fill="none"/>
      <circle cx="300" cy="204" r="9" fill="${palette.brim}"/>
      ${textEls}
    </svg>`;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);
  }

  // Turns "tone:dry|risk:medium|energy:wearable|" (from ac-product-json.liquid's
  // trait-{axis}-{value} tag parsing) into {tone:'dry', risk:'medium', energy:'wearable'}.
  // Any axis name works — this isn't limited to tone/risk/energy.
  function parseTraits(raw) {
    const traits = {};
    if (!raw) return traits;
    raw
      .split('|')
      .filter(Boolean)
      .forEach((pair) => {
        const [axis, value] = pair.split(':');
        if (axis && value) traits[axis] = value;
      });
    return traits;
  }

  function readProductData(scriptId) {
    const el = document.getElementById(scriptId);
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent);
      return data.map((p) => Object.assign({}, p, { traits: parseTraits(p.traits_raw) }));
    } catch (e) {
      console.error('ACShared: failed to parse product data', e);
      return [];
    }
  }

  /**
   * Adds a variant to cart using Dawn's native Ajax cart flow, then lets
   * Dawn's own cart-notification / cart-drawer render the response so the
   * rest of the store's cart UI stays fully native.
   */
  function addToCart(variantId, { onSuccess, onError } = {}) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement && cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    fetch(`${window.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'ac-shared',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          if (onError) onError(response.description || response.message);
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ac-shared',
          productVariantId: variantId,
          cartData: response,
        });

        if (cart) {
          cart.renderContents(response);
        }
        if (onSuccess) onSuccess(response);
      })
      .catch((e) => {
        console.error(e);
        if (onError) onError(e.message || 'Network error');
      });
  }

  return { splitPhrase, capSVG, readProductData, parseTraits, addToCart, escapeHtml };
})();

/* Site-wide: any product card marked data-ac-needs-placeholder (Dawn's shared
   card-product snippet, used on collection/search/related-product grids) gets
   the same illustrated cap fallback as the homepage shop grid and quiz.
   A MutationObserver (not just a DOMContentLoaded pass) is required because
   Dawn loads several of these grids asynchronously AFTER initial page load —
   product recommendations, cart drawer line items, quick-add modals — so
   their placeholder cards don't exist yet when the page first loads. */
(function () {
  function fillPlaceholders(root) {
    root.querySelectorAll('[data-ac-needs-placeholder]').forEach((media) => {
      if (media.dataset.acPlaceholderFilled) return;
      media.dataset.acPlaceholderFilled = 'true';
      media.innerHTML = window.ACShared.capSVG({
        title: media.dataset.productTitle || '',
        id: media.dataset.productId || '',
        image: null,
      });
    });
  }

  function init() {
    fillPlaceholders(document);
    if (!window.MutationObserver) return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-ac-needs-placeholder]')) {
            fillPlaceholders(node.parentElement || document);
          }
          if (node.querySelector) fillPlaceholders(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
